import { MAX_PHOTOS, MAX_PHOTO_BYTES, UPLOAD_TYPES, type PresignRequest, type PresignResponse, type VerifyResponse } from "@workcost/shared";
import { apiJson } from "./api";

export class UploadError extends Error {
  constructor(
    public readonly code: "empty" | "too_many" | "bad_type" | "too_large" | "put_failed" | "verify_failed" | "presign_mismatch",
    public readonly details?: unknown,
  ) {
    super(code);
    this.name = "UploadError";
  }
}

type UploadType = (typeof UPLOAD_TYPES)[number];

function asUploadType(t: string): UploadType {
  const found = UPLOAD_TYPES.find((x) => x === t);
  if (!found) throw new UploadError("bad_type", t);
  return found;
}

/**
 * อัปโหลดรูปแบบ presigned: POST /uploads/presign -> PUT ไป S3 -> POST /uploads/verify
 * คืน object key ตามลำดับรูปที่ส่งเข้ามา ไม่ log เนื้อรูป
 */
export async function uploadPhotos(photos: readonly Blob[], putFetch: typeof fetch = (...a) => fetch(...a)): Promise<string[]> {
  if (photos.length === 0) throw new UploadError("empty");
  if (photos.length > MAX_PHOTOS) throw new UploadError("too_many");
  for (const p of photos) {
    asUploadType(p.type);
    if (p.size > MAX_PHOTO_BYTES) throw new UploadError("too_large");
  }

  const request: PresignRequest = { files: photos.map((p) => ({ contentType: asUploadType(p.type), size: p.size })) };
  const presigned = await apiJson<PresignResponse>("/uploads/presign", { method: "POST", json: request });
  if (presigned.uploads.length !== photos.length) throw new UploadError("presign_mismatch");

  await Promise.all(
    presigned.uploads.map(async (u, i) => {
      const res = await putFetch(u.url, { method: "PUT", headers: u.headers, body: photos[i] as Blob });
      if (!res.ok) throw new UploadError("put_failed", { index: i, status: res.status });
    }),
  );

  const keys = presigned.uploads.map((u) => u.key);
  const verified = await apiJson<VerifyResponse>("/uploads/verify", { method: "POST", json: { keys } });
  const bad = verified.results.filter((r) => !r.ok);
  if (bad.length > 0 || verified.results.length !== keys.length) throw new UploadError("verify_failed", bad);
  return keys;
}
